import { PaginationState, SortingState } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { EntitySearchConfigsToolbar } from 'src/components/EntitySearch';
import { useEntitySearch } from 'src/components/EntitySearch';
import { EntitySearchRefetcherV2 } from 'src/components/EntitySearch/EntitySearchRefetcher/EntitySearchRefetcherV2';
import { InventoryFlattenedView } from 'src/components/Events/EventListing/InventoryEventListing/InventoryFlattenedView';
import { ListingSearchBox } from 'src/components/Listings/ListingFilterBar/ListingSearchBox';
import { SearchInfoBanner } from 'src/components/SearchInfoBanner';
import { ActivePosEntityProvider } from 'src/contexts/ActivePosEntityContext';
import {
  ActiveSortTableColumnProvider,
  TableViewMode,
} from 'src/contexts/ActiveSortTableColumnContext';
import { useAppContext } from 'src/contexts/AppContext';
import { CatalogDataContextProvider } from 'src/contexts/CatalogDataContext';
import { CatalogMultiSelectionContextProvider } from 'src/contexts/CatalogMultiSelectionContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { DialogProvider } from 'src/contexts/DialogContext/DialogContext';
import {
  FilterQueryContextProvider,
  useFilterQueryContext,
} from 'src/contexts/FilterQueryContext';
import { ListingStatusContextProvider } from 'src/contexts/ListingStatusContext';
import {
  MultiSelectionContextProvider,
  MultiSelectScope,
  NO_GROUP_ID,
} from 'src/contexts/MultiSelectionContext';
import {
  SearchDataContextProvider,
  useSearchDataContext,
} from 'src/contexts/SearchDataContext';
import { StopwatchLoggingContextProvider } from 'src/contexts/StopwatchLoggingContext';
import { PosNoResultsFound } from 'src/core/POS/PosNoResultsFound';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { DividerLine } from 'src/core/ui';
import { useGetActiveAccountClientConfig } from 'src/hooks/useGetActiveAccountClientConfig';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { LayoutContent } from 'src/navigations/LayoutContent';
import { createInventorySearchPageUrl } from 'src/utils/advancedSearchUtils';
import { ListingTableColumnId } from 'src/utils/columns/inventory/inventoryColumnUtils.types';
import { InventoryDeeplinkQueryParam } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { DefaultListingTableSorting } from 'src/utils/constants/tableColumns';
import { removeDeepLinkUrlPartsFromRelativeUrl } from 'src/utils/deepLinkUtils';
import {
  DefaultListingQueryV2,
  EmptyListingQueryV2,
  mapListingQueryToListingQueryV2,
} from 'src/utils/eventQueryUtils';
import { transformData } from 'src/utils/eventWithDataUtils';
import { EventPageLocationState } from 'src/utils/types/EventPageLocationState';
import { SomethingWentWrong } from 'src/views';
import {
  ActionOutboxEntityType,
  Feature,
  InventorySearchResult,
  InventoryViewMode,
  ListingClient,
  ListingDetailDataField,
  ListingDetails,
  ListingQueryV2,
  UISearchOptions,
  UserSetting,
} from 'src/WebApiController';

import {
  listingQueryValueTransformFromUrl,
  listingQueryValueTransformToUrl,
  searchListingsForEventsV2,
} from '../Inventory';
import { getDefaultReturnUrl } from '../InventoryEvent/constants';
import {
  ACTIVE_EVENT_IDS_2_QUERY_PARAM,
  ACTIVE_EVENT_IDS_QUERY_PARAM,
  TAB_EVENT_IDS_2_QUERY_PARAM,
  TAB_EVENT_IDS_QUERY_PARAM,
} from '../InventoryEvent/InventoryEvent.constants';
import { MainRoute } from '../MainRoute';
import { InventorySearchFilterBar } from './InventorySearchFilterBar';
import { InventorySearchToolbar } from './InventorySearchToolbar';
import * as styles from './InventorySearchV2.css';

export function InventorySearchV2() {
  const hasUnsoldTicketsFilterFeature = useUserHasFeature(
    Feature.UnsoldTicketsFilter
  );

  return (
    <FilterQueryContextProvider<ListingQueryV2>
      key={`inventory-search-${hasUnsoldTicketsFilterFeature}`}
      initialQuery={{
        ...DefaultListingQueryV2,
        viewMode: InventoryViewMode.FlattenedView,
        hasUnsoldTickets: hasUnsoldTicketsFilterFeature ? true : null,
      }}
      emptyQuery={EmptyListingQueryV2}
      queryValueTransformToUrl={listingQueryValueTransformToUrl}
      queryValueTransformFromUrl={listingQueryValueTransformFromUrl}
      saveQueryInUrl
    >
      <InventorySearchPage />
    </FilterQueryContextProvider>
  );
}

function InventorySearchPage() {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const [activeSearchConfig, setActiveSearchConfig] =
    useState<InventorySearchResult>();

  const getSearchData = useCallback(
    (ids: string[], f: ListingQueryV2) => {
      return searchListingsForEventsV2(ids, f, {
        activeAccountWebClientConfig,
        onError: (error) => {
          trackError('ListingClient.searchListingsForEventsV2', error, {
            ...f,
            eventIds: ids,
          });
        },
      });
    },
    [activeAccountWebClientConfig, trackError]
  );

  return (
    <StopwatchLoggingContextProvider>
      <CatalogDataContextProvider<ListingQueryV2>
        entityType={ActionOutboxEntityType.Listing}
        queryKey="getCatalogForListingOrders"
        transformEventData={transformData}
        disableAutoRefresh
        ignoreMaxCount
        doNotLogPageLoad
      >
        <SearchDataContextProvider<ListingQueryV2>
          queryKey="getSearchDataForListingOrders"
          entityType={ActionOutboxEntityType.Listing}
          getSearchData={getSearchData}
          disabled={activeSearchConfig == null}
        >
          <CatalogMultiSelectionContextProvider type={'listing'}>
            <MultiSelectionContextProvider
              isFlattenedView
              forceSelectionMode={{
                mode: MultiSelectScope.AllGroups,
                groupId: NO_GROUP_ID,
              }}
              initialAllGroupsIds={[NO_GROUP_ID]}
            >
              <InventorySearchContent
                activeSearchConfig={activeSearchConfig}
                setActiveSearchConfig={setActiveSearchConfig}
              />
            </MultiSelectionContextProvider>
          </CatalogMultiSelectionContextProvider>
        </SearchDataContextProvider>
      </CatalogDataContextProvider>
    </StopwatchLoggingContextProvider>
  );
}

function InventorySearchContent({
  activeSearchConfig,
  setActiveSearchConfig,
}: {
  activeSearchConfig: InventorySearchResult | undefined;
  setActiveSearchConfig: (a: InventorySearchResult | undefined) => void;
}) {
  const searchTitle = useContent(ContentId.SearchInventory);

  const { value: isEventGroupingEnabled } = useServerUserSetting<boolean>({
    id: UserSetting.GroupInventoryEventView,
  });

  const [localIsEventGroupingEnabled, setLocalIsEventGroupingEnabled] =
    useState(isEventGroupingEnabled ?? false);

  const location = useLocation();
  const navigate = useNavigate();
  const {
    errorInfo,
    isLoading: isSearchDataLoading,
    eventsTransformed,
    pitId,
    indexLastUpdatedAt,
  } = useSearchDataContext();

  const [pageIndex, setPageIndex] = useState(0);

  const {
    selectedFilters,
    setSelectedFilters,
    onSetActiveSearchConfig,
    onSaveSearchResult,
    onDeleteSearchResult,
    onRequeryForTheResult,
    refetchData,
    disabled,
    setDisabled,
  } = useEntitySearch(
    activeSearchConfig,
    setActiveSearchConfig,
    InventoryViewMode.FlattenedView,
    (client, id) => client.getInventorySearchResult(id),
    (client, r) => client.saveInventorySearchResult(r)
  );

  const { filterQuery, setFilterQuery } =
    useFilterQueryContext<ListingQueryV2>();

  // Only use value provided at the first time (should be from '/purchases')
  const returnUrl = useMemo(() => {
    const locationState = (location.state ?? {}) as EventPageLocationState;

    if (locationState.returnUrl && locationState.keepReturnUrlUnchanged) {
      return locationState.returnUrl;
    }

    const relativeUrl =
      locationState.returnUrl || getDefaultReturnUrl('purchases');

    return removeDeepLinkUrlPartsFromRelativeUrl(relativeUrl, [
      InventoryDeeplinkQueryParam,
      ACTIVE_EVENT_IDS_QUERY_PARAM,
      TAB_EVENT_IDS_QUERY_PARAM,
      ACTIVE_EVENT_IDS_2_QUERY_PARAM,
      TAB_EVENT_IDS_2_QUERY_PARAM,
    ]);
  }, [location.state]);

  const getActClientCfg = useGetActiveAccountClientConfig(
    'Inventory.ListingClient.getListingByListingId'
  );

  const getActivePosEntity = useCallback(
    async (
      posEntityId: number,
      curEntity?: ListingDetails | null,
      dataToGet?: string[] | null
    ) => {
      const activeClientCf = await getActClientCfg();

      const listingDetail = await new ListingClient(
        activeClientCf
      ).getListingByListingId(
        posEntityId,
        curEntity ?? undefined,
        dataToGet?.map((s) => s as ListingDetailDataField) ?? []
      );

      if (listingDetail) {
        return {
          posEntityId: listingDetail.id,
          posEntity: listingDetail,
          posEntityDisplayId: listingDetail.idOnMkp,
        };
      }

      return {};
    },
    [getActClientCfg]
  );

  const { refetch: refetchSearchData } = useSearchDataContext();

  const dataIsLoading = isSearchDataLoading;

  const onRedirectToSearchV1 = useCallback(() => {
    const searchUrl = createInventorySearchPageUrl(false);

    // Having small delay so the click is a bit more natural
    setTimeout(() => {
      navigate(searchUrl, {
        state: {
          returnUrl: returnUrl,
        },
      });
    }, 10);
  }, [navigate, returnUrl]);

  const onPaginationChange = useCallback(
    (pagination: PaginationState) => {
      setPageIndex(pagination.pageIndex);
      setFilterQuery({
        ...filterQuery,
        searchOptions: {
          ...((filterQuery.searchOptions ?? {}) as UISearchOptions),
          skip: pagination.pageIndex * pagination.pageSize,
          take: pagination.pageSize,
          pitId: pitId ?? null,
          includeAggregations: true,
        },
      });
    },
    [filterQuery, pitId, setFilterQuery]
  );

  const onSortingChange = useCallback(
    (sorting: SortingState) => {
      setFilterQuery({
        ...filterQuery,
        searchOptions: {
          ...((filterQuery.searchOptions ?? {}) as UISearchOptions),
          pitId: pitId ?? null,
          includeAggregations: true,
          sort: sorting[0]
            ? [
                {
                  field: sorting[0].id,
                  desc: sorting[0].desc,
                },
              ]
            : null,
        },
      });
    },
    [filterQuery, pitId, setFilterQuery]
  );

  const onSetActiveSearchConfigWrapper = useCallback(
    (searchConfig?: InventorySearchResult | undefined) => {
      const mappedSearchConfig: InventorySearchResult | undefined = searchConfig
        ? {
            ...searchConfig,
            query: null,
            queryV2:
              (searchConfig.query ?? searchConfig.queryV2)
                ? mapListingQueryToListingQueryV2(
                    searchConfig.query ?? searchConfig.queryV2!
                  )
                : null,
          }
        : undefined;

      onSetActiveSearchConfig(mappedSearchConfig);
    },
    [onSetActiveSearchConfig]
  );

  return (
    <ActivePosEntityProvider<ListingDetails>
      entityType={ActionOutboxEntityType.Listing}
      getActivePosEntity={getActivePosEntity}
    >
      <ListingStatusContextProvider>
        <ActiveSortTableColumnProvider
          entityType={ActionOutboxEntityType.Listing}
          viewMode={TableViewMode.SearchView}
          defaultSorting={DefaultListingTableSorting}
        >
          <DialogProvider>
            <LayoutContent
              mainRoute={MainRoute.InventorySearchV2}
              routeTitle={searchTitle}
              returnUrl={returnUrl}
              rightContent={
                <EntitySearchRefetcherV2 lastRefreshedAt={indexLastUpdatedAt} />
              }
              centerContent={
                <SearchInfoBanner
                  isOnSearchV2={true}
                  onRedirectClick={onRedirectToSearchV1}
                />
              }
            >
              <EntitySearchConfigsToolbar
                disabled={disabled}
                setDisabled={setDisabled}
                activeSearchConfig={activeSearchConfig}
                setActiveSearchConfig={onSetActiveSearchConfigWrapper}
                saveActiveSearchConfig={onSaveSearchResult}
                deleteActiveSearchConfig={onDeleteSearchResult}
                getSearchConfigs={(client, includeAllUser) =>
                  client.getInventorySearchConfigs(includeAllUser)
                }
                getSearchResult={(client, id) =>
                  client.getInventorySearchResult(id)
                }
                additionalActions={<ListingSearchBox />}
              />
              <DividerLine style={{ margin: 0 }} />
              <InventorySearchFilterBar
                activeSearchConfig={activeSearchConfig}
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
                onSubmitFilter={(filter) => {
                  if (!activeSearchConfig) {
                    // Setting an empty search config so it starts to filter
                    onSetActiveSearchConfig({
                      id: null,
                      name: null,
                      query: null,
                      queryV2: filter,
                      ownerUserId: null,
                      count: null,
                      entityIds: null,
                      lastFetchDate: null,
                    });
                  }
                  setPageIndex(0);
                  refetchData(filter, async () => refetchSearchData(), false);
                }}
              />
              <DividerLine
                style={{ margin: 0, borderColor: vars.color.borderLight }}
              />
              <InventorySearchToolbar
                disabled={disabled}
                activeSearchConfig={activeSearchConfig}
                setLocalIsEventGroupingEnabled={setLocalIsEventGroupingEnabled}
                localIsEventGroupingEnabled={localIsEventGroupingEnabled}
              />
              <DividerLine
                style={{ margin: 0, borderColor: vars.color.borderLight }}
              />
              <div className={styles.contentContainer}>
                {errorInfo && (
                  <SomethingWentWrong
                    header={errorInfo.errorHeader}
                    message={
                      errorInfo.errorMessage ?? (
                        <Content id={ContentId.FailToLoadListContent} />
                      )
                    }
                  />
                )}
                {activeSearchConfig == null ? (
                  <PosNoResultsFound
                    mainMessage={ContentId.SearchToSeeResults}
                  />
                ) : dataIsLoading ? (
                  <PosSpinner />
                ) : eventsTransformed?.length ? (
                  <InventoryFlattenedView
                    alwaysShowCheckbox
                    showSelectAllOnHeader
                    doNotShowToolbar
                    enableColumnFilters={true}
                    ignoreMaxCount
                    // Slightly hacky but reliable way to group by event info
                    // See ListingTableColumnsConfig.tsx [ListingTableColumnId.Checkbox] for more details
                    groupingStateReadonly={
                      localIsEventGroupingEnabled
                        ? [ListingTableColumnId.Checkbox]
                        : undefined
                    }
                    useTableV2
                    serverSidePagination
                    onPaginationChange={onPaginationChange}
                    onSortingChange={onSortingChange}
                    serverSidePageIndex={pageIndex}
                    isSearchV2
                  />
                ) : (
                  <PosNoResultsFound
                    detailMessage={ContentId.AddFilterToShowResult}
                  />
                )}
              </div>
            </LayoutContent>
          </DialogProvider>
        </ActiveSortTableColumnProvider>
      </ListingStatusContextProvider>
    </ActivePosEntityProvider>
  );
}
